<template>
  <v-data-table
    :headers="headers"
    :items="items"
    :show-expand="!isMobile()"
    :mobile-breakpoint="$vuetify.breakpoint.mobileBreakpoint"
    disable-sort
    disable-pagination
    hide-default-footer
    dense
    id="segmentTimePairTable"
    :class="{ 'segmentTable elevation-1': !isMobile() }"
    class="rounded-t-0 rounded-b"
  >
    <template v-slot:item.data-table-expand></template>
    <template v-slot:item.info="{ item }">
      <TimePairStatusIndicator :items="getRowIndicators(item)"/>
    </template>
    <template v-slot:item.modifiedBy="{ item }">
      <v-badge
        :value="item.modifiedBy.id === employeeId"
        :left="isMobile()"
        text
        inline
        color="success"
        :content="`${$t('misc.you')}`"
      >
        {{ formatName(item.modifiedBy.firstName, item.modifiedBy.lastName) }}
      </v-badge>
    </template>
    <template v-slot:item.startDate="{ item }">
      <slot name="startDate" v-bind:item="item">
        <StartDateTableCell :item="item"/>
      </slot>
    </template>
    <template v-slot:item.clockIn="{ item }">
      <slot name="clockIn" v-bind:item="item">
        <StartTimeTableCell :item="item"/>
      </slot>
    </template>
    <template v-slot:item.clockOut="{ item }">
      <slot name="clockOut" v-bind:item="item">
        <div v-if="!item.clockOut">
          -
        </div>
        <template v-else>
          <EndTimeTableCell :item="item" badge/>
        </template>
      </slot>
    </template>
    <template v-slot:item.duration="{ item }">
      {{ formatDuration(item) }}
    </template>
    <template v-slot:item.actions="{ item }">
      <slot name="actions" v-bind:item="item">
        <div class="font-italic text--disabled">Unavailable</div>
      </slot>
    </template>
  </v-data-table>
</template>

<script>
import { mapState } from 'vuex';

import capitalize from 'lodash/capitalize';

import TimePairStatusIndicator from '@/components/timepairs/TimePairStatusIndicator.vue';
import StartDateTableCell from '@/components/timepairs/shared/StartDateTableCell.vue';
import StartTimeTableCell from '@/components/timepairs/shared/StartTimeTableCell.vue';
import EndTimeTableCell from '@/components/timepairs/shared/EndTimeTableCell.vue';

import constants from '@/constants';
import DateTimeUtil from '@/util/date-time-util';
import getTableFooterProps from '@/mixins/getTableFooterProps';
import getTimePairStatusIndicator from '@/mixins/timepairs/getTimePairStatusIndicator';
import isMobile from '@/mixins/displayAdaptable';
import getTimePairClockingType from '@/mixins/timepairs/getTimePairClockingType';

export default {
  name: 'SegmentTimePairTable',

  components: {
    StartDateTableCell,
    StartTimeTableCell,
    EndTimeTableCell,
    TimePairStatusIndicator,
  },

  mixins: [
    getTableFooterProps,
    getTimePairClockingType,
    getTimePairStatusIndicator,
    isMobile,
  ],

  props: {
    headers: Array,
    items: Array,
  },

  computed: {
    ...mapState({
      employeeId: (state) => state.employee.id,
    }),
  },

  methods: {
    getRowIndicators(timePair) {
      const indicators = this.getTimePairClockingTypeIndicators(timePair);

      if (timePair.isInactive) {
        indicators.push(this.getIndicatorObject(
          'delete',
          'delete',
          constants.AppColors.ERROR,
          timePair.deletedDate ? `${capitalize(timePair.pairType)} deleted on
          ${DateTimeUtil.convertToHumanReadableDate(timePair.deletedDate)}`
            : this.$t('indicators.pendingDelete'),
        ));
      } else if (this.getTimePairClockingType(timePair) !== constants.TimePairType.CLOCK
        && timePair.previousVersionId == null) {
        indicators.push(this.getIndicatorObject(
          'new_segment',
          'add_circle_outline',
          `${constants.AppColors.SUCCESS} darken-2`,
          this.$t('indicators.newlyAdded'),
        ));
      }

      return indicators;
    },

    formatName(firstName, lastName) {
      return `${lastName} ${firstName.substring(0, 1)}.`;
    },

    formatDuration(timePair) {
      return DateTimeUtil.getFormattedDuration(timePair.roundedDuration);
    },
  },
};
</script>

<style scoped>
/*noinspection CssUnusedSymbol*/
.segmentTable {
  background-color: #fafafa;
}

/*noinspection CssUnusedSymbol*/
>>>.v-data-table-header {
  background-color: #e3e3e3;
  box-shadow: inset 0 4px 8px -5px rgba(50, 50, 50, 0.40);
}
</style>
